/**
 * API Keys page. Lists keys for a selected tenant and lets you create new ones
 * (role + collection scope). The plaintext key value is shown ONCE at creation
 * with a copy button; it is never retrievable again.
 */

import { Navigate } from "react-router-dom";
import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, Plus, Trash2, Copy, Check } from "lucide-react";
import { PageHeader } from "@/components/common/PageHeader";
import { ConfirmDangerButton } from "@/components/common/ConfirmDangerButton";
import { LoadingRow, EmptyState, ErrorState } from "@/components/common/StateViews";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { apiKeyApi, type ApiKey, type KeyRole } from "@/lib/proxy-api";
import { ApiException } from "@/lib/api-client";
import { canManageTenant, useTenantWorkspace } from "@/lib/tenant-workspace";

const KEY_ROLES: KeyRole[] = ["ADMIN", "WRITER", "READER"];

export function ApiKeysPage() {
  const qc = useQueryClient();
  const workspace = useTenantWorkspace();
  const selectedTenant = workspace.tenantId;
  const canManage = canManageTenant(workspace.role);

  const { data, isLoading, error } = useQuery({
    queryKey: ["api-keys", selectedTenant],
    queryFn: () => apiKeyApi.list(selectedTenant!),
    enabled: canManage && Boolean(selectedTenant),
  });

  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: "", role: "WRITER" as KeyRole, collectionIds: "" });
  const [createdKey, setCreatedKey] = useState<string | null>(null);
  const [createdKeyMissing, setCreatedKeyMissing] = useState(false);
  const [copied, setCopied] = useState(false);
  const [copyError, setCopyError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const createMut = useMutation({
    mutationFn: () =>
      apiKeyApi.create(selectedTenant!, {
        name: form.name,
        role: form.role,
        collection_ids: form.collectionIds ? form.collectionIds.split(",").map((s) => s.trim()) : ["*"],
      }),
    onSuccess: (res) => {
      qc.invalidateQueries({ queryKey: ["api-keys", selectedTenant] });
      setShowCreate(false);
      const plaintext = res.key ?? res.api_key ?? null;
      setCreatedKey(plaintext);
      setCreatedKeyMissing(!plaintext);
      setCopied(false);
      setCopyError(null);
      setForm({ name: "", role: "WRITER", collectionIds: "" });
      setFormError(null);
    },
    onError: (err) => setFormError(err instanceof ApiException ? err.message : "Failed to create key"),
  });

  const revokeMut = useMutation({
    mutationFn: (key: ApiKey) => apiKeyApi.revoke(selectedTenant!, key.id, {
      name: key.name,
      role: key.role,
      collection_ids: key.collection_ids,
      created_at: key.created_at,
    }),
    onMutate: () => setRevokeError(null),
    onSuccess: (_res, revokedKey) => {
      qc.setQueryData<{ keys: ApiKey[] }>(["api-keys", selectedTenant], (old) => {
        if (!old) return old;
        return {
          ...old,
          keys: old.keys.map((key) => String(key.id) === String(revokedKey.id) ? { ...key, is_active: false } : key),
        };
      });
      qc.invalidateQueries({ queryKey: ["api-keys", selectedTenant] });
    },
    onError: (err) => setRevokeError(err instanceof ApiException ? err.message : "Failed to revoke key"),
  });

  const keys: ApiKey[] = data?.keys ?? [];

  if (!canManage) return <Navigate to="/" replace />;

  return (
    <div className="space-y-6">
      <PageHeader
        title="API Keys"
        description="Upstream ServisSearch keys for the selected tenant. Plaintext is shown only once at creation."
        actions={
          selectedTenant ? (
            <Button onClick={() => { setShowCreate((v) => !v); setCreatedKey(null); setCreatedKeyMissing(false); setCopied(false); setCopyError(null); }}>
              <Plus className="h-4 w-4" />
              New key
            </Button>
          ) : undefined
        }
      />

      <CreatedKeyModal
        apiKey={createdKey}
        copied={copied}
        copyError={copyError}
        onCopy={async () => {
          if (!createdKey) return;
          try {
            await copyTextToClipboard(createdKey);
            setCopied(true);
            setCopyError(null);
          } catch {
            setCopied(false);
            setCopyError("Clipboard copy failed. Check browser clipboard permissions and try again.");
          }
        }}
        onDone={() => {
          if (!copied) return;
          setCreatedKey(null);
          setCopied(false);
          setCopyError(null);
        }}
      />

      {createdKeyMissing && (
        <div className="flex gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
          <AlertTriangle className="mt-0.5 h-4 w-4 text-destructive" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-destructive">Key created, but no plaintext secret was returned.</p>
            <p className="text-sm text-muted-foreground">
              The key may now exist upstream, but it cannot be recovered from the Admin UI. Revoke it from the list and create a replacement.
            </p>
          </div>
        </div>
      )}

      {revokeError && (
        <div className="flex gap-3 rounded-lg border border-destructive/30 bg-destructive/5 p-4">
          <AlertTriangle className="mt-0.5 h-4 w-4 text-destructive" />
          <div className="space-y-1">
            <p className="text-sm font-medium text-destructive">API key was not confirmed revoked.</p>
            <p className="text-sm text-muted-foreground">{revokeError}</p>
          </div>
        </div>
      )}

      {showCreate && (
        <form
          className="rounded-lg border bg-card p-4 space-y-3"
          onSubmit={(e) => { e.preventDefault(); createMut.mutate(); }}
        >
          <div className="grid gap-3 sm:grid-cols-3">
            <div className="space-y-1.5">
              <Label htmlFor="k-name">Name</Label>
              <Input id="k-name" value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required autoFocus />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="k-role">Key Role</Label>
              <select
                id="k-role"
                className="h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
                value={form.role}
                onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as KeyRole }))}
              >
                {KEY_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="k-coll">Collection IDs (comma-sep, or *)</Label>
              <Input id="k-coll" value={form.collectionIds} onChange={(e) => setForm((f) => ({ ...f, collectionIds: e.target.value }))} placeholder="*" />
            </div>
          </div>
          {formError && <p className="text-sm text-destructive">{formError}</p>}
          <div className="flex justify-end gap-2">
            <Button type="button" variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
            <Button type="submit" disabled={createMut.isPending || !form.name}>Create key</Button>
          </div>
        </form>
      )}

      {!selectedTenant ? (
        <EmptyState message="Select a tenant to view its API keys." />
      ) : isLoading ? (
        <LoadingRow cols={4} />
      ) : error ? (
        <ErrorState message={error instanceof ApiException ? error.message : "Failed to load keys"} />
      ) : keys.length === 0 ? (
        <EmptyState message="No API keys for this tenant yet." />
      ) : (
        <div className="rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Key Role</TableHead>
                <TableHead>Collections</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="w-16 text-right">Revoke</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {keys.map((k) => (
                <TableRow key={k.id} className={!k.is_active ? "bg-muted/30 opacity-70" : ""}>
                  <TableCell className={!k.is_active ? "font-medium text-muted-foreground line-through" : "font-medium"}>{k.name}</TableCell>
                  <TableCell><KeyRoleBadge role={k.role} /></TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {k.collection_ids?.includes("*") ? "all" : (k.collection_ids?.length ?? 0)}
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={k.is_active ? "bg-green-50 text-green-700 border-green-200" : "bg-muted text-muted-foreground"}>
                      {k.is_active ? "active" : "revoked"}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <ConfirmDangerButton
                      title="Revoke API key"
                      resourceName={k.name}
                      actionLabel="Revoke key"
                      disabled={!k.is_active || revokeMut.isPending}
                      onConfirm={() => revokeMut.mutate(k)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </ConfirmDangerButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

function KeyRoleBadge({ role }: { role: KeyRole }) {
  const tone =
    role === "ADMIN"
      ? "bg-primary/10 text-primary border-primary/20"
      : role === "WRITER"
        ? "bg-amber-50 text-amber-700 border-amber-200"
        : "bg-blue-50 text-blue-700 border-blue-200";
  return <Badge variant="outline" className={tone}>{role}</Badge>;
}

function CreatedKeyModal({
  apiKey,
  copied,
  copyError,
  onCopy,
  onDone,
}: {
  apiKey: string | null;
  copied: boolean;
  copyError: string | null;
  onCopy: () => Promise<void>;
  onDone: () => void;
}) {
  if (!apiKey) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/85 p-4" role="dialog" aria-modal="true" aria-labelledby="created-key-title">
      <div className="flex max-h-[88vh] w-full max-w-3xl flex-col rounded-lg border bg-card shadow-lg">
        <div className="border-b p-4">
          <h2 id="created-key-title" className="font-semibold">API key created</h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Copy this secret now. The Admin UI cannot retrieve it again after this modal is closed.
          </p>
        </div>

        <div className="space-y-4 overflow-auto p-4">
          <div className="rounded-md border bg-muted/30 p-3">
            <code className="block break-all font-mono text-sm">{apiKey}</code>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <Button type="button" variant={copied ? "outline" : "default"} onClick={() => void onCopy()}>
              {copied ? <Check className="h-4 w-4 text-green-600" /> : <Copy className="h-4 w-4" />}
              {copied ? "Copied to clipboard" : "Copy key"}
            </Button>
            <Button type="button" onClick={onDone} disabled={!copied}>
              {copied ? "Done" : "Copy key first"}
            </Button>
          </div>

          {copyError && <p className="text-sm text-destructive">{copyError}</p>}
          <p className="text-xs text-muted-foreground">
            Clicking outside this modal will not close it. It only closes after a successful clipboard copy.
          </p>
        </div>
      </div>
    </div>
  );
}

async function copyTextToClipboard(text: string): Promise<void> {
  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(text);
    return;
  }

  const textarea = document.createElement("textarea");
  textarea.value = text;
  textarea.setAttribute("readonly", "true");
  textarea.style.position = "fixed";
  textarea.style.left = "-9999px";
  document.body.appendChild(textarea);
  textarea.select();
  try {
    const copied = document.execCommand("copy");
    if (!copied) throw new Error("copy command failed");
  } finally {
    document.body.removeChild(textarea);
  }
}
